/**
 * Design-system chat empty state shared by the floating chat surfaces: the
 * Sparkles "What should we get done?" card over a row of suggested-action
 * pills. The hint line and the action set are per-surface; the canvas hides
 * the pills during onboarding (`showSuggestions`) so its pinned overlay prompt
 * stays the only call-to-action.
 */

import { SuggestionPills } from "@cohesive/ui-components";
import { Sparkles, type LucideIcon } from "lucide-react";
import { motion } from "motion/react";

/** A prefill pill: `label` on the pill, `message` sent when it's clicked. */
export interface FloatingChatSuggestedAction {
  id: string;
  label: string;
  message: string;
  icon: LucideIcon;
}

export function FloatingChatEmptyState({
  hint,
  actions,
  onAction,
  showSuggestions = true,
}: {
  /** The one-liner under the headline (per-surface @-mention wording). */
  hint: string;
  actions: FloatingChatSuggestedAction[];
  onAction: (action: FloatingChatSuggestedAction) => void;
  /** Hide the pills (keeping the card) — e.g. during canvas onboarding. */
  showSuggestions?: boolean;
}) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center p-4 overflow-y-auto">
      <div className="max-w-measure text-center font-sans text-md text-n-60">
        <p className="flex items-center justify-center gap-2">
          <Sparkles className="size-4 text-rust" /> What should we get done?
        </p>
        <p className="mt-1 text-xs text-n-50">{hint}</p>
      </div>

      {showSuggestions && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
          className="mt-6 max-w-sm"
        >
          <SuggestionPills
            className="justify-center"
            suggestions={actions.map((action) => ({
              label: action.label,
              icon: action.icon,
            }))}
            onSelect={(suggestion) => {
              const action = actions.find((a) => a.label === suggestion.label);
              if (action) onAction(action);
            }}
          />
        </motion.div>
      )}
    </div>
  );
}
